import {User} from '../../auth/symbols';
import {SubmissionDetailedStatus, SubmissionScanQuality} from '../../dashboard/symbols/submission.symbols';
import {DashboardViewRegion} from "../../dashboard-view-regions/types/dashboard-view-regions.types";

/**
 * Represents a user in the dashboard context.
 * Currently used on the patient list page.
 * TODO: implement in backend user API.
 */
export interface DashboardUser {
  /** Unique user ID. */
  uid: User['uid'];

  /** User's first name. */
  first_name: User['first_name'];

  /** User's last name. */
  last_name: User['last_name'];

  /** User's email address. */
  email: User['email'];

  /** User's sex (e.g., 'M' or 'F'). */
  sex: DashboardUserSex;

  /** User's date of birth (ISO format). */
  date_of_birth: string;

  /**
   * Whether the user has access to the dashboard.
   * A single account may represent a clinic patient who isn't interested in the BrainKey platform.
   * In this case, they won't be invited and the access property will be false.
   */
  access: boolean;

  /** List of studies linked to the user. */
  related_studies: DashboardStudyShort[];
}

/**
 * Short data about the study.
 * Used to display the study on the patient list page (in the expandable panel).
 * A "study" in medical imaging (DICOM) refers to a collection of related scans for a patient visit.
 * TODO: implement in the backend short study API.
 */
export interface DashboardStudyShort {
  /** Unique study ID. */
  id: number;

  /**
   * Study acquisition date in ISO format.
   * Corresponds to when the scan was taken.
   */
  study_date: string;

  /**
   * Whether the study has been inspected by a reviewer.
   * Used by clinics to track the review process.
   * Have no effect on the study itself or on our processing pipeline.
   */
  is_inspected: boolean;

  /** Detailed status of the study (e.g., processing or completed). */
  status_description: SubmissionDetailedStatus;

  /**
   * Progress (0–100) of filling additional data about patient condition in the study.
   * Can contain information like blood pressure, heart rate, etc.
   */
  additional_series_data_progress: number;

  /**
   * Access type for the study.
   */
  access_type: DashboardStudyAccessType;

  /** Reports and results linked to the study's scan series. */
  reports: DashboardSeriesReports;
}

/**
 * Full representation of a user study.
 * Used in the dashboard study viewer.
 * TODO: implement in backend detailed study API.
 * TODO: send public study for users without private studies.
 */
export interface DashboardStudy<T = DashboardViewRegion> extends DashboardStudyShort {
  /**
   * Series (scans) associated with the study.
   * A "series" is a single imaging acquisition (e.g., one MRI sequence).
   */
  related_series: DashboardSeries[];

  /** Viewable anatomical or functional regions in this study. */
  view_regions: T[];

  /** ID of the user associated with this study (UUID format). */
  user_id: string;

  /** User's first name (duplicated for display context). */
  first_name: string;

  /** User's last name (duplicated for display context). */
  last_name: string;

  /** User's age at the time of the scan. */
  age_at_scan: string;

  /** User's sex (e.g., 'M' or 'F'). */
  sex: DashboardUserSex;

  /** ID of the derived AR model used for this study. */
  ar_derivative_id: number;
}

/**
 * Represents a single scan series within a study.
 * A "series" corresponds to one specific scan type, like T1 or FLAIR.
 */
export interface DashboardSeries {
  /** URL to the original base image from the scan. */
  base_image_url: string;

  /** URL to the image containing segmentations/labels. */
  label_image_url: string;

  /** Quality score or label for the scan. */
  quality: SubmissionScanQuality;

  /** Scan modality/class, e.g., T1, T2, FLAIR. */
  class: DashboardSeriesScanClass;

  /** Processing or availability status of this scan. */
  status_description: SubmissionDetailedStatus;
}

/**
 * Collection of reports and their metadata for a scan series.
 * These reports include visualizations, scores, and derived results.
 */
export interface DashboardSeriesReports {
  /** Brain age estimation report. */
  brain_age: DashboardSeriesReportMetadata;

  /** Report showing key anatomical layers or structures. */
  key_layer: DashboardSeriesReportMetadata;

  /** Perivascular spaces report (fluid-filled spaces around vessels). */
  pvs: DashboardSeriesReportMetadata;

  /** White matter hyperintensities report (lesions linked to aging, disease). */
  whm: DashboardSeriesReportMetadata;

  /**
   * Enlarged ventricles report (may indicate brain atrophy).
   * Optional report, present only if the Ventricles region are out of the typical range.
   */
  evr: DashboardSeriesReportMetadata;

  /** Combined shortened data from the BrainAge and KeyLayer reports. */
  executive: DashboardSeriesReportMetadata;

  /** BrainAge, KeyLayer, PVS, and WHM reports merged as the single PDF file. */
  all_reports: DashboardSeriesReportMetadata;
}

/**
 * Metadata for a single report.
 * Indicates availability, visibility, and location of the report.
 */
export interface DashboardSeriesReportMetadata {
  /** Display title of the report. */
  view_name: string;

  /** URL to view or download the report. */
  url: string;

  /** Whether the report data is available. */
  is_available: boolean;

  /** Reason for report being unavailable (if any). */
  unavailability_reason: string;

  /** Whether the report is currently shown in the UI. */
  is_visible: boolean;
}

/**
 * Enumerates available scan classes (MRI modalities).
 * T1, T2, FLAIR, etc., are MRI sequence types capturing different tissue contrasts.
 */
export enum DashboardSeriesScanClass {
  /** T1-weighted brain scan (high anatomical detail). */
  T1Brain = 't1',

  /** T1-weighted whole-body scan. */
  T1WholeBody = 't1_whole_body',

  /** T2-weighted scan (fluid appears bright). */
  T2 = 't2',

  /** FLAIR sequence (removes fluid signal to highlight lesions). */
  Flair = 'flair',

  /** Susceptibility-weighted imaging (detects blood, iron, microbleeds). */
  Swi = 'swi',

  /** Time-of-flight angiography (visualizes blood flow). */
  Tof = 'tof',
}

/**
 * Describes possible derivative types for the scan images.
 */
export enum DashboardSeriesDerivativeType {

  /** Base scan image; no derivative applied. */
  Base = 'Base',

  /** Labeled scan image; derivative applied. */
  Labeled = 'Labeled',
}

/**
 * Enumerates possible access types for a study.
 */
export enum DashboardStudyAccessType {
  /** The study is user's own. */
  Private = 'private',

  /** The study is shared with the user. */
  Shared = 'shared',

  /** The study is public and accessible to everyone. */
  Public = 'public',
}

/**
 * Describes possible options for the user's sex field.
 */
export enum DashboardUserSex {
  Male = 'M',
  Female = 'F',
}
